
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAdminById } from '@/services/db/admins';
import { Admin } from '@/types/admin.types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Edit, Printer } from 'lucide-react';
import { printAdminProfileAsJPG, printAdminProfileAsPDF } from '@/utils/printing/administrative';
import AdminProfileCard from '@/components/admin/details/AdminProfileCard';
import AdminContactCard from '@/components/admin/details/AdminContactCard';
import AdminQualificationsTab from '@/components/admin/details/AdminQualificationsTab';
import AdminDocumentsTab from '@/components/admin/details/AdminDocumentsTab';
import AdminHistoryTab from '@/components/admin/details/AdminHistoryTab';
import AdminFormatDialog from '@/components/admin/AdminFormatDialog';
import PageLoader from '@/components/common/PageLoader';

const AdminDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showFormatDialog, setShowFormatDialog] = useState(false);

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!id) {
          console.error('❌ No admin ID provided in URL params');
          setError('Admin ID is required');
          return;
        }

        console.log(`🔍 AdminDetails: Fetching admin details for URL ID: "${id}"`);
        console.log(`📍 Current URL: ${window.location.pathname}`);
        
        // Call getAdminById with the clean ID from URL (no # prefix)
        const data = await getAdminById(id);
        
        if (data) {
          console.log(`✅ AdminDetails: Admin found successfully:`, { 
            id: data.id, 
            name: `${data.firstName} ${data.lastName}` 
          });
          setAdmin(data);
        } else {
          console.log(`❌ AdminDetails: Admin not found for ID: "${id}"`);
          setError('Administrator not found');
        }
      } catch (err) {
        console.error('❌ AdminDetails: Error fetching admin:', err);
        setError('Failed to load administrator details');
      } finally {
        setLoading(false);
      }
    };

    fetchAdmin();
  }, [id]);

  const handlePrintProfile = async (format: 'jpg' | 'pdf') => {
    if (admin) {
      console.log(`🖨️ Starting ${format.toUpperCase()} download for admin profile...`);
      try {
        if (format === 'jpg') {
          await printAdminProfileAsJPG(admin);
        } else {
          await printAdminProfileAsPDF(admin);
        }
        console.log(`✅ ${format.toUpperCase()} download completed successfully`);
      } catch (error) {
        console.error(`❌ Error downloading admin profile ${format.toUpperCase()}:`, error);
      }
    }
  };

  if (loading) {
    return <PageLoader message="Loading administrator details..." />;
  }

  if (error || !admin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Administrator Not Found</h2>
            <p className="text-gray-600 mb-6">{error || 'The administrator you\'re looking for does not exist.'}</p>
            <Button onClick={() => navigate("/admins")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Administrators
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <Button variant="outline" onClick={() => navigate("/admins")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Administrators
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowFormatDialog(true)}
          >
            <Printer className="w-4 h-4 mr-2" />
            Download Profile
          </Button>
          <Button onClick={() => navigate(`/admins/edit/${id}`)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left column - Basic Info */}
        <div className="space-y-6">
          <AdminProfileCard admin={admin} />
          <AdminContactCard admin={admin} />
        </div>

        {/* Right column - Tabs */}
        <div className="md:col-span-2">
          <Tabs defaultValue="qualifications">
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="qualifications">Qualifications</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="history">Activity History</TabsTrigger>
            </TabsList>

            <TabsContent value="qualifications">
              <AdminQualificationsTab qualifications={admin.qualifications || []} />
            </TabsContent>

            <TabsContent value="documents">
              <AdminDocumentsTab documents={admin.documents || []} />
            </TabsContent>

            <TabsContent value="history">
              <AdminHistoryTab history={admin.history || []} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <AdminFormatDialog
        isOpen={showFormatDialog}
        onClose={() => setShowFormatDialog(false)}
        onDownload={handlePrintProfile}
        downloadType="profile"
        adminName={`${admin.firstName} ${admin.lastName}`}
      />
    </div>
  );
};

export default AdminDetails;
